import { inject, observer } from 'mobx-react';
import * as React from 'react';

import { merge } from 'bernie-core';

import { FormOptions, FormQueryVariables, LodgingForm } from '@shared-ui/lodging-form';
import { RefTarget } from '@shared-ui/ref-context';

import { CurrentHotelStore } from 'src/common/stores/current-hotel-store';
import { SearchCriteriaModel } from 'src/common/stores/models/search-criteria-model';

import { UitkSpacing } from 'uitk-react-spacing';

import { ActionProvider, OnChangeFn, OnErrorFn, OnSubmitFn } from './action-provider';
import { alwaysIgnoredKeys } from 'components/common/search-form';
import { useFormInputs } from './use-form-inputs';

interface SharedUiFormWrapperProps {
  currentHotel?: CurrentHotelStore;
  onChange?: OnChangeFn;
  onSubmit?: OnSubmitFn;
  onError?: OnErrorFn;
  action?: string;
  formRef?: React.RefObject<HTMLFormElement>;
}

export const HiddenFields: React.FC<{ searchCriteria?: SearchCriteriaModel }> = ({ searchCriteria }) => {
  const [excludedFields, setExcludedFields] = React.useState(['latLong', 'regionId', 'x_pwa']);

  React.useEffect(() => {
    setExcludedFields([]);
  }, []);

  const hiddenFields = Object.keys(
    merge.withoutKeys([...alwaysIgnoredKeys, ...excludedFields, 'startDate', 'endDate', 'rooms']).merge({}, searchCriteria)
  );

  return (
    <>
      {hiddenFields.map(field => {
        const value = searchCriteria?.[field] || '';
        const disabled = value === '' || (typeof value?.length === 'number' && value.length === 0);

        return <input key={`hidden-field-${field}`} type="hidden" name={field} value={value} disabled={disabled} />;
      })}
    </>
  );
};

export const SharedUiFormWrapper: React.FC<SharedUiFormWrapperProps> = inject('currentHotel')(
  observer(({ currentHotel, onChange, onSubmit, onError, action, children, formRef }) => {
    const { hotelId, searchCriteria } = currentHotel || {};

    const formData: FormOptions['data'] = React.useMemo(
      () => ({
        method: 'GET',
        action: action || '',
        id: 'pdp-search-form',
      }),
      [action]
    );
    const inputs = hotelId && searchCriteria ? useFormInputs(hotelId, searchCriteria) : ({} as FormQueryVariables);

    // Hidden fields need a few extra values for noJS
    const hiddenFieldsSearchCriteria = searchCriteria?.clone();

    if (!hotelId || !searchCriteria) {
      return null;
    }

    return (
      <UitkSpacing margin={{ blockend: 'two' }}>
        <div>
          <ActionProvider
            formAction={action || ''}
            searchCriteria={searchCriteria}
            onChange={onChange}
            onSubmit={onSubmit}
            onError={onError}
          >
            <RefTarget name="FormWrapper" data-stid="rooms-and-rates">
              <LodgingForm data={formData} inputs={inputs} formRef={formRef}>
                {children}
                <HiddenFields searchCriteria={hiddenFieldsSearchCriteria} />
              </LodgingForm>
            </RefTarget>
          </ActionProvider>
        </div>
      </UitkSpacing>
    );
  })
);
